import type { AnswerRecord, BattleMeta, Format, Item, NumberSpec } from './types';
import { MAX_POINTS } from './types';

/** The gap between clues in a 'clues' question when the item does not say. */
export const DEFAULT_CLUE_INTERVAL_MS = 5000;

/** Up to `max` points for speed, falling linearly to nothing at the deadline. Answers stamped before the start count as instant. */
export function speedPoints(elapsedMs: number, durationMs: number, max: number): number {
  if (!(durationMs > 0)) return 0;
  const t = Math.max(0, elapsedMs);
  return Math.round(max * Math.max(0, 1 - t / durationMs));
}

/** From the second consecutive right answer: 50 per answer in the run beyond the first, capped at 250. */
export function streakBonus(streak: number): number {
  if (streak < 2) return 0;
  return Math.min(50 * (streak - 1), 250);
}

const clueCount = (n: number | undefined) => Math.max(1, Math.floor(n ?? 1) || 1);

/**
 * Clues on the table `elapsedMs` into a 'clues' question: the first at the start, then one per interval, never more
 * than there are. Given the answer window, a clue due at or after the deadline is never counted, as the engine never
 * deals it, so an answer in the grace window is not charged for a clue nobody saw.
 */
export function cluesShownAt(elapsedMs: number, clues: number, intervalMs: number = DEFAULT_CLUE_INTERVAL_MS, durationMs?: number): number {
  const step = intervalMs > 0 ? intervalMs : DEFAULT_CLUE_INTERVAL_MS;
  const dealt = typeof durationMs === 'number' && durationMs > 0 ? Math.max(1, Math.ceil(durationMs / step)) : Infinity;
  return Math.min(clueCount(clues), dealt, Math.floor(Math.max(0, elapsedMs) / step) + 1);
}

/** Points for a right 'clues' answer locked in with `shown` clues dealt: 1000, 800, 600, 400, 200 for five clues worth 1000. */
export function cluePoints(maxPoints: number, clues: number, shown: number): number {
  const n = clueCount(clues);
  const s = Math.min(n, Math.max(1, Math.floor(shown) || 1));
  return Math.round((maxPoints * (n - s + 1)) / n);
}

export interface ScoreInput {
  format: Format;
  correct: boolean;
  /** Server receipt time minus the question's server start. */
  elapsedMs: number;
  durationMs: number;
  /** Consecutive right answers before this one. */
  streakBefore: number;
  /** 'clues' only: how many clues the item deals. */
  clues?: number;
  /** 'clues' only: the gap between clues (default 5 s). */
  clueIntervalMs?: number;
  /** 2 for double points; applies to the whole item score, streak bonus included. */
  multiplier?: number;
  /** The item's maximum before any streak bonus or multiplier; defaults to the format's (1000, true or false 500). */
  maxPoints?: number;
}
export interface Score {
  points: number;
  streak: number;
  /** The streak bonus inside `points`, after the multiplier. */
  bonus: number;
}

/**
 * One answer's points. mcq, gmcq and spot: half the maximum for being right plus up to half for speed (500 + 500);
 * true or false the same on a 500 maximum (250 + 250); clues: the maximum stepped down by the clues dealt when the
 * answer locked (no speed bonus). The streak bonus is added, then the multiplier scales the lot. Wrong scores nothing
 * and resets the streak.
 */
export function scoreAnswer(input: ScoreInput): Score {
  if (!input.correct) return { points: 0, streak: 0, bonus: 0 };
  const max = typeof input.maxPoints === 'number' && Number.isFinite(input.maxPoints) && input.maxPoints > 0 ? input.maxPoints : MAX_POINTS[input.format] ?? 1000;
  const streak = Math.max(0, input.streakBefore || 0) + 1;
  const bonus = streakBonus(streak);
  let earned: number;
  if (input.format === 'clues') {
    const n = clueCount(input.clues);
    earned = cluePoints(max, n, cluesShownAt(input.elapsedMs, n, input.clueIntervalMs, input.durationMs));
  } else {
    const base = Math.round(max / 2);
    earned = base + speedPoints(input.elapsedMs, input.durationMs, max - base);
  }
  const m = typeof input.multiplier === 'number' && Number.isFinite(input.multiplier) && input.multiplier > 0 ? input.multiplier : 1;
  return { points: Math.round((earned + bonus) * m), streak, bonus: Math.round(bonus * m) };
}

// ---------------------------------------------------------------------------
// v2.2: formats that are not "pick one". Every format keeps the same shape: half the item's maximum for a right
// answer plus up to half for speed, the speed bonus only on full credit, the multiplier over the lot.
// - number: full within the tolerance, then the base falls linearly to nothing across the span.
// - order: full for the exact order; 60% of the base when the only slip is one swap of two neighbouring steps.
// - multi 'all': the base × (right picked − wrong picked) ÷ number right, floored at zero; full only when exactly right.
// - hotspot: as mcq, and any right region counts.
// - multi 'rarity': nothing at answer time; the close pays each right option by how few chose it (meta.bonus).
// Partial credit ends the streak; only full credit (or a right rarity pick) carries it on.
// ---------------------------------------------------------------------------

/** How much of the answer a number, an order or a pick-all earned: all of it, some of it, or none. */
export type Credit = 'full' | 'partial' | 'none';
/** The share of the base an order answer keeps when one swap of two neighbouring steps is its only slip. */
export const ORDER_NEAR_MISS = 0.6;
/** A right rarity pick earns at least this percentage of the item's maximum, however many chose it. */
export const RARITY_FLOOR = 10;

const positive = (n: unknown): n is number => typeof n === 'number' && Number.isFinite(n) && n > 0;
/** Own keys only (never Object.prototype's), without Object.hasOwn, which older in-app browsers lack. */
const own = (o: object, key: string) => Object.prototype.hasOwnProperty.call(o, key);

/** Credit for a typed number between 0 and 1: 1 within ± tolerance, then falling linearly to 0 across the span. */
export function numberCredit(value: number, spec: Pick<NumberSpec, 'answer' | 'tolerance' | 'span'>): number {
  if (typeof value !== 'number' || !Number.isFinite(value) || !Number.isFinite(spec.answer)) return 0;
  const tolerance = Math.max(0, Number.isFinite(spec.tolerance) ? spec.tolerance : 0);
  // Keypad decimals are not exact in binary (2.42 − 2.37 is not quite 0.05), so a hair either way counts as the boundary.
  const eps = 1e-9 * Math.max(1, Math.abs(spec.answer));
  const off = Math.abs(value - spec.answer);
  if (off <= tolerance + eps) return 1;
  if (!positive(spec.span)) return 0;
  const credit = 1 - (off - tolerance) / spec.span;
  return credit <= eps ? 0 : Math.min(1, credit);
}

/** An order of step ids against the right one: full when equal, partial when one swap of two neighbours apart, else none. */
export function orderCredit(given: readonly string[], correct: readonly string[]): Credit {
  if (!Array.isArray(given) || given.length !== correct.length || correct.length === 0) return 'none';
  const differ: number[] = [];
  for (let i = 0; i < correct.length; i++) if (given[i] !== correct[i]) differ.push(i);
  if (differ.length === 0) return 'full';
  const [i, j] = differ;
  return differ.length === 2 && j === i + 1 && given[i] === correct[j] && given[j] === correct[i] ? 'partial' : 'none';
}

/** Picks against the right options, each option counted once: fraction = (right − wrong) ÷ number right, floored at zero. */
export function multiCredit(picked: readonly string[], correctIds: readonly string[]): { right: number; wrong: number; fraction: number; exact: boolean } {
  const truth = new Set(correctIds);
  let right = 0; let wrong = 0;
  for (const id of new Set(Array.isArray(picked) ? picked : [])) { if (truth.has(id)) right++; else wrong++; }
  const fraction = truth.size > 0 ? Math.max(0, (right - wrong) / truth.size) : 0;
  return { right, wrong, fraction, exact: truth.size > 0 && right === truth.size && wrong === 0 };
}

/** What a phone sends for one question (AnswerRequest without the index). */
export interface AnswerInput { optionId?: string; value?: number; optionIds?: readonly string[]; order?: readonly string[] }
export type ScorableItem = Pick<Item, 'format' | 'correctId' | 'durationMs' | 'maxPoints'>
  & Partial<Pick<Item, 'clues' | 'clueIntervalMs' | 'multiplier' | 'number' | 'hotspot' | 'order' | 'multi'>>;
export interface ItemScore extends Score {
  correct: boolean;
  credit: Credit;
  /** What the answer record stores as optionId: the pick for single-pick formats, the credit bucket for number, order and multi 'all'. */
  optionId: string;
}

/** Number, order and multi 'all' store a credit bucket as the answer's optionId; every other format stores the pick. */
export const isCreditFormat = (item: Pick<Item, 'format'> & Partial<Pick<Item, 'multi'>>): boolean =>
  item.format === 'number' || item.format === 'order' || (item.format === 'multi' && item.multi?.mode !== 'rarity');

/** A right answer as scoreAnswer scores it (base, speed, streak bonus, multiplier), or the base scaled by partial credit. */
function byCredit(item: ScorableItem, fraction: number, elapsedMs: number, streakBefore: number): ItemScore {
  const max = positive(item.maxPoints) ? item.maxPoints : MAX_POINTS[item.format] ?? 1000;
  if (fraction >= 1) {
    const s = scoreAnswer({ format: item.format, correct: true, elapsedMs, durationMs: item.durationMs, streakBefore, maxPoints: max, multiplier: item.multiplier });
    return { ...s, correct: true, credit: 'full', optionId: 'full' };
  }
  const earned = Math.round(Math.round(max / 2) * Math.max(0, fraction));
  if (earned <= 0) return { correct: false, credit: 'none', optionId: 'none', points: 0, streak: 0, bonus: 0 };
  const m = positive(item.multiplier) ? item.multiplier : 1;
  return { correct: false, credit: 'partial', optionId: 'partial', points: Math.round(earned * m), streak: 0, bonus: 0 };
}

/**
 * One answer to an item of any format, scored from the item itself (its spec, maximum, multiplier, clues). The phone's
 * answer is assumed to have been checked against the item already (ids exist, no duplicates, value within bounds).
 */
export function scoreItem(item: ScorableItem, answer: AnswerInput, elapsedMs: number, streakBefore: number): ItemScore {
  switch (item.format) {
    case 'number':
      return byCredit(item, item.number && typeof answer.value === 'number' ? numberCredit(answer.value, item.number) : 0, elapsedMs, streakBefore);
    case 'order': {
      const credit = item.order && Array.isArray(answer.order) ? orderCredit(answer.order, item.order.correctOrder) : 'none';
      return byCredit(item, credit === 'full' ? 1 : credit === 'partial' ? ORDER_NEAR_MISS : 0, elapsedMs, streakBefore);
    }
    case 'multi':
      if (item.multi?.mode === 'rarity') {
        const optionId = answer.optionId ?? '';
        const right = item.multi.correctIds.includes(optionId);
        return { correct: right, credit: right ? 'full' : 'none', optionId, points: 0, streak: right ? Math.max(0, streakBefore || 0) + 1 : 0, bonus: 0 };
      }
      return byCredit(item, item.multi ? multiCredit(answer.optionIds ?? [], item.multi.correctIds).fraction : 0, elapsedMs, streakBefore);
    default: {
      const optionId = answer.optionId ?? '';
      const right = item.format === 'hotspot' && item.hotspot ? item.hotspot.correctIds.includes(optionId) : optionId === item.correctId;
      const s = scoreAnswer({
        format: item.format, correct: right, elapsedMs, durationMs: item.durationMs, streakBefore, maxPoints: item.maxPoints,
        clues: item.format === 'clues' && Array.isArray(item.clues) ? item.clues.length : undefined, clueIntervalMs: item.clueIntervalMs,
        multiplier: item.multiplier,
      });
      return { ...s, correct: right, credit: right ? 'full' : 'none', optionId };
    }
  }
}

// ---------------------------------------------------------------------------
// Rarity: the close works out what share of the answering room chose each option, and each right option pays
// max(10, 100 − its percentage) per cent of the item's maximum, times its multiplier. The points sit in
// meta.bonus['q<index>'][optionId]; every score anyone sees adds them with scoreWithBonus (which also takes voided
// questions off, see below).
// ---------------------------------------------------------------------------

/** Whole percentages of the players who answered, per option (0 for all when nobody answered). */
export function rarityPercentages(counts: Readonly<Record<string, number>>, answered: number): Record<string, number> {
  const out: Record<string, number> = {};
  for (const [id, n] of Object.entries(counts)) out[id] = answered > 0 ? Math.round((100 * n) / answered) : 0;
  return out;
}

/** Points for a right rarity pick that `percentage` of the room also chose. */
export function rarityPoints(percentage: number, maxPoints?: number, multiplier?: number): number {
  const pct = Number.isFinite(percentage) ? Math.min(100, Math.max(0, percentage)) : 0;
  const max = positive(maxPoints) ? maxPoints : 1000;
  const m = positive(multiplier) ? multiplier : 1;
  return Math.round((Math.max(RARITY_FLOOR, 100 - pct) / 100) * max * m);
}

/** The points each right option of a rarity question pays, from the room's percentages. */
export function rarityBonus(correctIds: readonly string[], rarity: Readonly<Record<string, number>>, maxPoints?: number, multiplier?: number): Record<string, number> {
  const out: Record<string, number> = {};
  for (const id of correctIds) out[id] = rarityPoints(own(rarity, id) ? rarity[id] : 0, maxPoints, multiplier);
  return out;
}

/** One answer's rarity points: what `optionId` paid at question `q`'s close, or 0 (always 0 for a voided question). */
export function bonusFor(bonus: BattleMeta['bonus'], q: string, optionId: string | undefined): number {
  if (!bonus || optionId === undefined || !own(bonus, q) || isVoidedQ(bonus, q)) return 0;
  const points = bonus[q];
  const v = points && own(points, optionId) ? points[optionId] : undefined;
  return typeof v === 'number' && Number.isFinite(v) && v > 0 ? v : 0;
}

/** The answer fields a score as shown needs: the pick (for rarity points) and, for a voided question, what it scored. */
export type ShownAnswer = Pick<AnswerRecord, 'optionId'> & Partial<Pick<AnswerRecord, 'points'>>;

/**
 * A player's score as everyone sees it: the stored score plus the rarity points of every right rarity pick, less the
 * points of every voided question (marked in meta.bonus, see VOID_MARK). Never below zero.
 */
export function scoreWithBonus(
  score: number | undefined, answers: Readonly<Record<string, ShownAnswer | undefined>> | undefined, bonus: BattleMeta['bonus'],
): number {
  let total = typeof score === 'number' && Number.isFinite(score) ? score : 0;
  if (!bonus || !answers) return total;
  for (const q of Object.keys(bonus)) {
    const a = own(answers, q) ? answers[q] : undefined;
    if (!a) continue;
    if (isVoidedQ(bonus, q)) total -= typeof a.points === 'number' && Number.isFinite(a.points) && a.points > 0 ? a.points : 0;
    else total += bonusFor(bonus, q, a.optionId);
  }
  return Math.max(0, total);
}

// ---------------------------------------------------------------------------
// Voided questions (hardening, 5 Oct). A voided question scores nothing for anyone: Skip voids the open question, End
// voids a question it cuts short, and the producer can void a closed one (a wrongly keyed question). The battle lists
// them in meta.voided (what the reveal and the views show); for scoring, each also leaves a mark in meta.bonus,
// bonus['q<index>'] = { '#void': 1 } in place of any rarity points, so every score worked out with scoreWithBonus (the
// leaderboard, GET /me, the live views, the leads export and the prize draw) takes the question's points off. A skipped
// question gets its mark at its close: until then it is still the open question, and the views that show the score
// as it stood before the open question take its points off themselves. Streaks step over a voided question: it
// neither breaks nor extends one (streakBefore).
// ---------------------------------------------------------------------------
/** The key a voided question's entry in meta.bonus carries. It is never an option id (those are letters, row ids, leads or credit buckets). */
export const VOID_MARK = '#void';

/** True when meta.bonus marks question `q` ('q<index>') as voided. */
export function isVoidedQ(bonus: BattleMeta['bonus'], q: string): boolean {
  if (!bonus || !own(bonus, q)) return false;
  const points = bonus[q];
  return !!points && typeof points === 'object' && own(points, VOID_MARK);
}

/** meta.bonus with question `index` marked voided (any rarity points it paid are dropped with it). */
export function withVoidMark(bonus: BattleMeta['bonus'], index: number): NonNullable<BattleMeta['bonus']> {
  return { ...(bonus ?? {}), [`q${index}`]: { [VOID_MARK]: 1 } };
}

/** meta.bonus with every index in `voided` marked; the very same object when nothing needed marking. */
export function withVoids(bonus: BattleMeta['bonus'], voided: readonly number[] | undefined): BattleMeta['bonus'] {
  let out = bonus;
  for (const i of Array.isArray(voided) ? voided : []) {
    if (Number.isInteger(i) && i >= 0 && !isVoidedQ(out, `q${i}`)) out = withVoidMark(out, i);
  }
  return out;
}

/** The question indexes meta.bonus marks as voided, in order. */
export function voidedIndexes(bonus: BattleMeta['bonus']): number[] {
  return Object.keys(bonus ?? {}).filter((q) => /^q\d+$/.test(q) && isVoidedQ(bonus, q)).map((q) => Number(q.slice(1))).sort((a, b) => a - b);
}

/**
 * The streak going into question `index`: right answers (full credit, or a right rarity pick) counted back from the last
 * answered question before it. A question not answered is stepped over (it leaves the stored streak alone), and so is a
 * voided one (it neither breaks nor extends a streak); a wrong answer ends the count.
 */
export function streakBefore(
  answers: Readonly<Record<string, Pick<AnswerRecord, 'correct'> | undefined>> | undefined, index: number, voided?: ReadonlySet<number>,
): number {
  let n = 0;
  for (let i = index - 1; i >= 0; i--) {
    if (voided?.has(i)) continue;
    const a = answers && own(answers, `q${i}`) ? answers[`q${i}`] : undefined;
    if (!a) continue;
    if (!a.correct) break;
    n++;
  }
  return n;
}
